import type {
  ColorByCategory,
  ColorByOption,
} from 'components/color-by-attribute-modal';
import { WELL_STATUS_LABELS } from 'types/well-workspace';
import {
  WELLBORE_GEOMETRY_ATTRIBUTE,
  wellboreGeometryOptions,
} from 'components/wells-color-by';
import type { WellsResponse } from 'types/api-responses';

/**
 * The attributes the Well page can colour by.
 *
 * The mock's own list for this page, shorter than a workspace's because grouping one
 * well by state or county says nothing. Wellbore Geometry is the odd one: its values
 * are kinds of trace, so a rule on it colours this well's bores against each other.
 */
export const WELL_PAGE_COLOR_ATTRIBUTES: ColorByCategory[] = [
  { id: 'status', label: 'Well Status' },
  { id: 'currentOperator', label: 'Current Operator' },
  { id: 'srpLandingZone', label: 'SIM Landing Zone' },
  { id: WELLBORE_GEOMETRY_ATTRIBUTE, label: 'Wellbore Geometry' },
];

/**
 * The values the Well page offers for one attribute.
 *
 * Well Status comes from the six-value enum rather than this well, because the rule
 * also paints neighbouring wells that carry every status. The other attributes have no
 * enum and so offer what this well is, which is thin on a one-well page but honest.
 * @param {WellsResponse | undefined} well The subject well.
 * @param {string} attributeId The attribute to read.
 * @return {ColorByOption[]} Its values, ordered for reading.
 */
export const wellPageColorOptions = (
  well: WellsResponse | undefined,
  attributeId: string
): ColorByOption[] => {
  if (attributeId === 'status') {
    return Object.entries(WELL_STATUS_LABELS)
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  // A fixed vocabulary of trace kinds, not something this well carries a value for.
  if (attributeId === WELLBORE_GEOMETRY_ATTRIBUTE) {
    return wellboreGeometryOptions();
  }

  const raw = well
    ? (well as unknown as Record<string, unknown>)[attributeId]
    : undefined;
  if (raw === null || raw === undefined || raw === '') return [];

  const id = String(raw);

  // `status` returned above with its labels; every other attribute reads as its raw value.
  return [{ id, name: id }];
};
